import { useEffect, useRef } from 'react';

type ScrollCallback = () => void;

// Single shared scroll listener that fans out to all registered callbacks.
const listeners = new Set<ScrollCallback>();
let raf: number | null = null;

function onScroll() {
  if (raf !== null) return;
  raf = requestAnimationFrame(() => {
    raf = null;
    listeners.forEach((cb) => cb());
  });
}

let listenerAttached = false;

function attachListener() {
  if (listenerAttached) return;
  window.addEventListener('scroll', onScroll, { passive: true });
  listenerAttached = true;
}

function detachListener() {
  if (listeners.size > 0) return;
  window.removeEventListener('scroll', onScroll);
  if (raf !== null) {
    cancelAnimationFrame(raf);
    raf = null;
  }
  listenerAttached = false;
}

export function useRAFScroll(callback: ScrollCallback): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const cb: ScrollCallback = () => callbackRef.current();
    attachListener();
    listeners.add(cb);
    // Fire once on mount to set initial positions.
    callbackRef.current();

    return () => {
      listeners.delete(cb);
      detachListener();
    };
  }, []);
}
